import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { useSurvey } from '@hooks/register/surveys/use-survey';
import { useLocalize } from '@hooks/use-localize';
import { useSession } from '@hooks/security/use-session';
import { useStepForm } from '@hooks/use-step-form';
import { createSurveySchema } from '@views/register/surveys/survey.schema';
import type { ISurveyFormValues, ISurveyModel, ISurveySubmitResult } from '@/types/surveys/survey';
import PageLoader from '@components/page-loader';
import FieldError from '@components/field-error';
import StarRating from '@views/register/surveys/star-rating';
import AgreementScale from '@views/register/surveys/agreement-scale';

const Survey = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useSurvey(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <SurveyContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface ISurveyContentProps {
    model: ISurveyModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: ISurveyFormValues) => Promise<ISurveySubmitResult>;
}

const SurveyContent = ({ model, isSubmitting, submitError, onSubmit }: ISurveyContentProps) => {
    const t = useLocalize(model.languageData);
    const navigate = useNavigate();

    const schema = useMemo(
        () =>
            createSurveySchema({
                askReimbursementQuestion: model.askReimbursementQuestion,
                requireIncomeRange: model.requireIncomeRange,
            }),
        [model.askReimbursementQuestion, model.requireIncomeRange],
    );

    const {
        register,
        watch,
        setValue,
        onSubmit: handleFormSubmit,
        formState: { errors },
    } = useStepForm<ISurveyFormValues, ISurveySubmitResult>({
        resolver: zodResolver(schema),
        defaultValues: {
            surveySurgeonCare: '',
            surveyNurseCare: '',
            surveyPainCare: '',
            surveyCaretakerTreatment: '',
            annualIncomeRange: '',
            surveyDonationWasFinancialBurden: '',
            surveyDonationCostWasImportant: '',
            surveyNeededReimbursement: '',
            commendations: '',
            comments: '',
        },
        submit: onSubmit,
        // The survey sits outside the screening / history step maps, so it routes itself.
        linkMap: [],
        onSuccess: () => navigate('/register/donate'),
    });

    const requiredFields: (keyof ISurveyFormValues)[] = [
        'surveySurgeonCare',
        'surveyNurseCare',
        'surveyPainCare',
        'surveyCaretakerTreatment',
        'surveyDonationWasFinancialBurden',
        'surveyDonationCostWasImportant',
        'commendations',
        'comments',
        ...(model.requireIncomeRange ? (['annualIncomeRange'] as const) : []),
        ...(model.askReimbursementQuestion ? (['surveyNeededReimbursement'] as const) : []),
    ];
    const values = watch();
    const answered = requiredFields.filter((name) => Boolean(values[name])).length;

    return (
        <div className='container'>
            <div className="row">
                <div className="col-xs-12">
                    <h1>{t('DashLabel_PleaseGiveUsFeedbackOnYourSurgery', 'Please tell us about your surgery')}</h1>
                    <p>
                        {t(
                            'DashLabel_OnAScaleOf1To51BeingTheLowestScoreAnd5BeingTheHighSurgical',
                            'On a scale of 1 to 5 (1 being the lowest score and 5 being the highest score) please rate the following aspects of your surgical experience',
                        )}
                        .
                    </p>

                    <form className="form survey-form" onSubmit={handleFormSubmit}>
                        <h2 className="survey-section__title">{t('DashLabel_YourCare', 'Your Care')}</h2>

                        <div className="form-group">
                            <label className="control-label col-xs-12">
                                <FieldError message={errors.surveySurgeonCare?.message} />{' '}
                                {t('DashLabel_HowWellWereYouCaredForByYourSurgeon', 'How well were you cared for by your surgeon')}?
                            </label>
                            <div className="col-xs-12 col-sm-8">
                                <StarRating
                                    field={register('surveySurgeonCare')}
                                    value={watch('surveySurgeonCare')}
                                    onClear={() => setValue('surveySurgeonCare', '')}
                                    t={t}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-xs-12">
                                <FieldError message={errors.surveyNurseCare?.message} />{' '}
                                {t('DashLabel_HowWellWereYouCaredForByTheNursingStaff', 'How well were you cared for by the nursing staff')}?
                            </label>
                            <div className="col-xs-12 col-sm-8">
                                <StarRating
                                    field={register('surveyNurseCare')}
                                    value={watch('surveyNurseCare')}
                                    onClear={() => setValue('surveyNurseCare', '')}
                                    t={t}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-xs-12">
                                <FieldError message={errors.surveyPainCare?.message} />{' '}
                                {t('DashLabel_HowWouldYouRateYourPainManagement', 'How would you rate your pain management')}?
                            </label>
                            <div className="col-xs-12 col-sm-8">
                                <StarRating
                                    field={register('surveyPainCare')}
                                    value={watch('surveyPainCare')}
                                    onClear={() => setValue('surveyPainCare', '')}
                                    t={t}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-xs-12">
                                <FieldError message={errors.surveyCaretakerTreatment?.message} />{' '}
                                {t('DashLabel_HowWellWasYourCaretakerTreated', 'How well was your caretaker treated')}?
                            </label>
                            <div className="col-xs-12 col-sm-8">
                                <StarRating
                                    field={register('surveyCaretakerTreatment')}
                                    value={watch('surveyCaretakerTreatment')}
                                    onClear={() => setValue('surveyCaretakerTreatment', '')}
                                    t={t}
                                />
                            </div>
                        </div>

                        <h2 className="survey-section__title">{t('DashLabel_FinancialImpact', 'Financial Impact')}</h2>

                        {model.requireIncomeRange && (
                            <div className="form-group">
                                <label className="control-label col-xs-12">
                                    <FieldError message={errors.annualIncomeRange?.message} />{' '}
                                    {t('DashLabel_WhatIsYourAnnualIncome', 'What is your annual income')}?
                                </label>
                                <div className="col-xs-12">
                                    {model.incomeRanges.map((range) => (
                                        <div className="radio" key={range.value}>
                                            <label>
                                                <input
                                                    type="radio"
                                                    value={range.value}
                                                    {...register('annualIncomeRange')}
                                                />{' '}
                                                {range.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label className="control-label col-xs-12">
                                <FieldError message={errors.surveyDonationWasFinancialBurden?.message} />{' '}
                                {t(
                                    'DashLabel_OrganDonationWasAFinancialBurdenToMeAndMyFamily',
                                    'Organ donation was a financial burden to me and my family',
                                )}
                                .
                            </label>
                            <div className="col-xs-12 col-sm-8">
                                <AgreementScale
                                    field={register('surveyDonationWasFinancialBurden')}
                                    value={watch('surveyDonationWasFinancialBurden')}
                                    onClear={() => setValue('surveyDonationWasFinancialBurden', '')}
                                    t={t}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-xs-12">
                                <FieldError message={errors.surveyDonationCostWasImportant?.message} />{' '}
                                {t(
                                    'DashLabel_TheCostToMeOfOrganDonation',
                                    'The cost to me of organ donation was important when I was deciding whether to donate',
                                )}
                                .
                            </label>
                            <div className="col-xs-12 col-sm-8">
                                <AgreementScale
                                    field={register('surveyDonationCostWasImportant')}
                                    value={watch('surveyDonationCostWasImportant')}
                                    onClear={() => setValue('surveyDonationCostWasImportant', '')}
                                    t={t}
                                />
                            </div>
                        </div>

                        {model.askReimbursementQuestion && (
                            <div className="form-group">
                                <label className="control-label col-xs-12">
                                    <FieldError message={errors.surveyNeededReimbursement?.message} />{' '}
                                    {t(
                                        'DashLabel_WouldYouHaveBeenAbleToDonateWithoutLostWageReimbur',
                                        'Would you have been able to donate without lost wage reimbursement',
                                    )}
                                    ?
                                </label>
                                {/* The answers are stored as "needed reimbursement", so able to donate without it is a No. */}
                                <div className="col-xs-12 col-sm-8">
                                    <label className="radio-inline">
                                        <input type="radio" value="0" {...register('surveyNeededReimbursement')} />{' '}
                                        {t('Word_Yes', 'Yes')}
                                    </label>
                                    <label className="radio-inline">
                                        <input type="radio" value="1" {...register('surveyNeededReimbursement')} />{' '}
                                        {t('Word_No', 'No')}
                                    </label>
                                    <label className="radio-inline">
                                        <input type="radio" value="-1" {...register('surveyNeededReimbursement')} />{' '}
                                        {t('DashLabel_IDonTKnow', "I don't know")}
                                    </label>
                                </div>
                            </div>
                        )}

                        <h2 className="survey-section__title">{t('DashLabel_YourComments', 'Your Comments')}</h2>

                        <div className="form-group">
                            <label className="control-label col-xs-12" htmlFor="Commendations">
                                <FieldError message={errors.commendations?.message} />{' '}
                                {t(
                                    'DashLabel_WasThereAnyoneWhoDidAnEspecially',
                                    'Was there anyone who did an especially great job taking care of you that you would like to commend',
                                )}
                                ?
                            </label>
                            <div className="col-xs-12">
                                <textarea
                                    id="Commendations"
                                    className="form-control"
                                    rows={5}
                                    maxLength={1000}
                                    {...register('commendations')}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-xs-12" htmlFor="Comments">
                                <FieldError message={errors.comments?.message} />{' '}
                                {t('DashLabel_HowCanWeImprove', 'How can we improve')}?
                            </label>
                            <div className="col-xs-12">
                                <textarea
                                    id="Comments"
                                    className="form-control"
                                    rows={5}
                                    maxLength={1000}
                                    {...register('comments')}
                                />
                            </div>
                        </div>

                        {submitError && (
                            <div className="form-group">
                                <div className="col-xs-12">
                                    <div className="alert alert-danger">{submitError}</div>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <div className="col-xs-12">
                                <div className="survey-progress" aria-hidden="true">
                                    <div
                                        className="survey-progress__bar"
                                        style={{ width: `${(answered / requiredFields.length) * 100}%` }}
                                    />
                                </div>
                                <p className="survey-progress__text">
                                    {answered}/{requiredFields.length} {t('DashLabel_Answered', 'answered')}
                                </p>
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    {t('Word_Submit', 'Submit')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Survey;
